import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { CalendarBlank, MapPin, Ticket, ArrowLeft, PencilSimple, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import UserLink from "../components/UserLink";
import AddToCalendarButton from "../components/AddToCalendarButton";
import {
  Header, ScopeTabs, Loading, Empty, NotFound, Countdown,
  FormField, FormModal, inputCls,
} from "./MeetupsPage";

const CATEGORIES = [
  ["all", "All"], ["community", "Community"], ["concert", "Concert"],
  ["networking", "Networking"], ["volunteer", "Volunteer"], ["sports", "Sports"],
  ["food", "Food"], ["class", "Class"], ["other", "Other"],
];

/**
 * EventsPage — public happenings (concerts, networking, community, etc.).
 * /app/events        → list + filters + create
 * /app/events/:eid   → detail with RSVP + attendees
 */
export default function EventsPage() {
  const { eid } = useParams();
  return eid ? <EventDetail eid={eid}/> : <EventsList/>;
}


function EventsList() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("upcoming");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events2", { params: { scope, category } });
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, category]);

  const create = async (payload) => {
    const { data } = await api.post("/events2", payload);
    setCreateOpen(false);
    navigate(`/app/events/${data.event_id}`);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="events-list">
      <Header title="Events" sub="Command Center · Events" actionLabel="Post Event"
              onAction={() => setCreateOpen(true)} testid="events"/>
      <div className="flex items-center gap-3 flex-wrap">
        <ScopeTabs scope={scope} onChange={setScope}
                   options={[["upcoming","Upcoming"],["mine","Mine"],["past","Past"]]}
                   testid="events-scope"/>
        <div className="flex items-center gap-1 flex-wrap">
          {CATEGORIES.map(([k, l]) => (
            <button key={k} onClick={() => setCategory(k)}
                    data-testid={`events-cat-${k}`}
                    className="h-8 px-3 rounded-full text-[10px] uppercase tracking-widest"
                    style={category === k
                      ? { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }
                      : { border: "1px solid rgba(212,175,55,0.4)", color: "#F2DC8C" }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Loading/>
        : items.length === 0 ? <Empty label="Nothing on the calendar yet." onCreate={() => setCreateOpen(true)} cta="Post an Event" icon={CalendarBlank}/>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
            {items.map((e) => <EventTile key={e.event_id} ev={e}/>)}
          </div>
        )}

      <AnimatePresence>
        {createOpen && <EventModal onClose={() => setCreateOpen(false)} onSave={create}/>}
      </AnimatePresence>
    </div>
  );
}


function EventTile({ ev }) {
  return (
    <Link to={`/app/events/${ev.event_id}`} data-testid={`event-tile-${ev.event_id}`}
          className="rounded-2xl p-4 block transition-all hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
            border: "1px solid rgba(212,175,55,0.45)",
          }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span className="inline-block text-[8px] uppercase tracking-widest mb-1 px-1.5 py-0.5 rounded"
                style={{ border: "1px solid rgba(212,175,55,0.55)", color: "#FFD700" }}>
            {ev.category}
          </span>
          <div className="font-display text-[17px] text-[#FFD700] leading-tight truncate">{ev.title}</div>
          <div className="mt-1 text-[11px] uppercase tracking-widest text-[#F2DC8C]/60 flex items-center gap-1">
            <CalendarBlank size={11}/> {new Date(ev.event_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
          <div className="text-[11px] text-[#F2DC8C]/70 flex items-center gap-1 mt-0.5 truncate">
            <MapPin size={11}/> {ev.location}
          </div>
        </div>
        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <CalendarBlank size={14} weight="fill" color="#0a0703"/>
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#F2DC8C]/60">
        <span>{ev.rsvp_count} going</span>
        {ev.ticket_price && (
          <span className="inline-flex items-center gap-1 text-[#FFD700]">
            <Ticket size={10}/> {ev.ticket_price}
          </span>
        )}
      </div>
    </Link>
  );
}


function EventDetail({ eid }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/events2/${eid}`); setE(data); } catch { setE(null); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eid]);

  const isOwner = e && user && e.owner_id === user.user_id;
  const toggleRsvp = async () => {
    setBusy(true);
    try {
      if (e.is_rsvpd) await api.delete(`/events2/${eid}/rsvp`);
      else await api.post(`/events2/${eid}/rsvp`);
      load();
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events2/${eid}`);
    navigate("/app/events");
  };
  const save = async (payload) => {
    await api.put(`/events2/${eid}`, payload); setEditOpen(false); load();
  };

  if (loading) return <Loading/>;
  if (!e) return <NotFound label="Event not found" backTo="/app/events"/>;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="event-detail">
      <Link to="/app/events" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 hover:text-[#FFD700]">
        <ArrowLeft size={12}/> All Events
      </Link>
      <div className="rounded-2xl p-6 text-center"
           style={{
             background: "radial-gradient(circle at 50% 0%, rgba(255,215,0,0.14) 0%, transparent 55%)," +
                         "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
             border: "1px solid rgba(255,215,0,0.4)",
             boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
           }}>
        <span className="inline-block text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
          {e.category} · Event
        </span>
        <h1 className="mt-3 font-display text-[30px] leading-tight"
            style={{
              background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
          {e.title}
        </h1>
        <Countdown target={e.event_at}/>
        <div className="mt-3 text-[13px] text-[#F2DC8C] space-y-0.5">
          <div><span className="text-[#FFD700] font-display">When:</span> {new Date(e.event_at).toLocaleString()}</div>
          <div><span className="text-[#FFD700] font-display">Where:</span> {e.location}</div>
          {e.ticket_price && <div><span className="text-[#FFD700] font-display">Ticket:</span> {e.ticket_price}</div>}
          <div><span className="text-[#FFD700] font-display">Host:</span> <UserLink username={e.owner_username}>@{e.owner_username}</UserLink></div>
        </div>
        {e.description && (
          <p className="mt-4 text-[13px] text-[#F2DC8C]/80 leading-relaxed whitespace-pre-wrap max-w-2xl mx-auto">
            {e.description}
          </p>
        )}
        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          {!isOwner && (
            <button onClick={toggleRsvp} disabled={busy} data-testid="event-rsvp"
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-xl font-display uppercase text-[12px] tracking-[0.22em] disabled:opacity-50"
                    style={e.is_rsvpd
                      ? { border: "1px solid rgba(212,175,55,0.55)", color: "#F2DC8C" }
                      : { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703",
                          boxShadow: "0 6px 20px rgba(255,215,0,0.35)" }}>
              {e.is_rsvpd ? "Cancel RSVP" : "RSVP"}
            </button>
          )}
          {e.link_url && (
            <a href={e.link_url} target="_blank" rel="noopener noreferrer" data-testid="event-link"
               className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest text-[#FFD700]"
               style={{ border: "1px solid rgba(212,175,55,0.55)" }}>
              <ArrowSquareOut size={11}/> Info
            </a>
          )}
          {isOwner && (
            <>
              <button onClick={() => setEditOpen(true)} data-testid="event-edit"
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest"
                      style={{ border: "1px solid rgba(212,175,55,0.55)", color: "#F2DC8C" }}>
                <PencilSimple size={11}/> Edit
              </button>
              <button onClick={remove} data-testid="event-delete"
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest text-[#FF5D73]"
                      style={{ border: "1px solid rgba(255,93,115,0.4)" }}>
                <Trash size={11}/> Delete
              </button>
            </>
          )}
          <AddToCalendarButton
            title={e.title}
            start={e.event_at}
            description={e.description}
            location={e.location}
            testId="event-gcal"
          />
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-[0.28em] text-[#F2DC8C]/75">
        Going · {e.rsvp_count}
      </div>

      <AnimatePresence>
        {editOpen && <EventModal ev={e} onClose={() => setEditOpen(false)} onSave={save}/>}
      </AnimatePresence>
    </div>
  );
}


function EventModal({ ev, onClose, onSave }) {
  const [title, setTitle] = useState(ev?.title || "");
  const [description, setDescription] = useState(ev?.description || "");
  const [location, setLocation] = useState(ev?.location || "");
  const [when, setWhen] = useState(ev?.event_at ? ev.event_at.slice(0, 16) : "");
  const [ticket, setTicket] = useState(ev?.ticket_price || "");
  const [linkUrl, setLinkUrl] = useState(ev?.link_url || "");
  const [category, setCategory] = useState(ev?.category || "community");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || !location.trim() || !when) return;
    setBusy(true);
    try {
      await onSave({
        title, description: description || null, location, event_at: when,
        ticket_price: ticket || null, link_url: linkUrl || null, category,
      });
    } finally { setBusy(false); }
  };

  return (
    <FormModal title={ev ? "Edit Event" : "Post Event"} icon={CalendarBlank}
               onClose={onClose} onSubmit={submit} busy={busy}
               disabled={!title.trim() || !location.trim() || !when}
               saveLabel={ev ? "Save" : "Post"} testid="event-modal">
      <FormField label="Title">
        <input autoFocus data-testid="event-title" value={title}
               onChange={(e) => setTitle(e.target.value.slice(0, 180))}
               placeholder="Sunset drum circle" className={inputCls}/>
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="When">
          <input type="datetime-local" data-testid="event-when" value={when}
                 onChange={(e) => setWhen(e.target.value)}
                 className={inputCls} style={{ colorScheme: "dark" }}/>
        </FormField>
        <FormField label="Category">
          <select data-testid="event-cat" value={category}
                  onChange={(e) => setCategory(e.target.value)} className={inputCls}
                  style={{ colorScheme: "dark" }}>
            {CATEGORIES.filter(([k]) => k !== "all").map(([k, l]) => (
              <option key={k} value={k} style={{ background: "#0a0703" }}>{l}</option>
            ))}
          </select>
        </FormField>
      </div>
      <FormField label="Location">
        <input data-testid="event-loc" value={location}
               onChange={(e) => setLocation(e.target.value.slice(0, 300))}
               placeholder="Kaanapali Beach, Maui" className={inputCls}/>
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Ticket (optional)">
          <input data-testid="event-ticket" value={ticket}
                 onChange={(e) => setTicket(e.target.value.slice(0, 60))}
                 placeholder="Free / $25" className={inputCls}/>
        </FormField>
        <FormField label="Info link (optional)">
          <input data-testid="event-link" value={linkUrl}
                 onChange={(e) => setLinkUrl(e.target.value.slice(0, 600))}
                 placeholder="https://…" className={inputCls}/>
        </FormField>
      </div>
      <FormField label="Description (optional)">
        <textarea data-testid="event-desc" value={description} rows={4}
                  onChange={(e) => setDescription(e.target.value.slice(0, 8000))}
                  placeholder="Details, vibe, what to bring."
                  className={inputCls + " resize-none"}/>
      </FormField>
    </FormModal>
  );
}
